import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as React from "react";
import IconButton from "src/components/IconButton";
import { cn } from "src/lib/cn";

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef(
  /**
   * @param {Object} props
   * @param {string} [props.className]
   * @param {("always-dark" | "auto" | "none")} [props.backdropColor]
   * @param {React.Ref<HTMLDivElement>} ref
   * @returns {JSX.Element}
   */
  ({ className, backdropColor, ...props }, ref) => (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-[99999999]",
        backdropColor === "none" ? "bg-transparent backdrop-blur-none" : "backdrop-blur-[2px]",
        backdropColor === "always-dark" ? "bg-black/50" : backdropColor === "none" ? "bg-transparent" : "bg-app-focus-overlay-background",
        "data-[state=open]:animate-in data-[state=closed]:animate-out duration-500",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      )}
      {...props}
    />
  )
);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef(
  /**
   * @param {Object} props
   * @param {React.ReactNode} props.children
   * @param {string} [props.className]
   * @param {("always-dark" | "auto" | "none")} [props.backdropColor]
   * @param {boolean} [props.hideClose] - Whether to hide the close button
   * @param {string} [props.overlayClassName]
   * @param {React.CSSProperties} [props.style]
   * @param {React.Ref<HTMLDivElement>} ref
   * @returns {JSX.Element}
   */
  ({ className, children, hideClose, backdropColor, overlayClassName, ...props }, ref) => (
    <DialogPortal>
      <DialogOverlay backdropColor={backdropColor} className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "box-border translate-x-0 translate-y-0 scale-100 scale-x-100 scale-y-100 skew-x-0 skew-y-0 rotate-0",
          "fixed left-[50%] top-[50%] z-[999999999]",
          "w-full max-w-lg",
          "translate-x-[-50%] translate-y-[-50%]",
          "bg-app-menu-color",
          "p-6",
          "shadow-card",
          "border border-app-menu-color-hover",
          "sm:rounded-lg",

          "duration-200",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-0",
          "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
          "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",

          className
        )}
        {...props}
      >
        {children}
        {!hideClose && (
          <DialogPrimitive.Close asChild>
            <IconButton
              size={40}
              iconSize={20}
              smartFill
              src="/assets/close.svg"
              className={cn("!absolute right-3 top-3", "text-app-grey-0", "opacity-70 transition-opacity", "hover:opacity-100", "disabled:pointer-events-none")}
            >
              <span className="sr-only">Close</span>
            </IconButton>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
const DialogHeader = ({ className, ...props }) => (
  <div
    className={cn("flex flex-col space-y-1.5", "text-center sm:text-left", "!leading-none", className)}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
const DialogFooter = ({ className, ...props }) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row", "gap-2 sm:justify-end", "mt-6", className)}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef(
  /**
   * @param {Object} props
   * @param {React.ReactNode} props.children
   * @param {string} [props.className]
   * @param {React.Ref<HTMLHeadingElement>} ref
   * @returns {JSX.Element}
   */
  ({ className, ...props }, ref) => (
    <DialogPrimitive.Title
      ref={ref}
      className={cn("text-xl font-medium text-app-grey-0", "!leading-none !m-0", "!text-app-cta-text-color", className)}
      {...props}
    />
  )
);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef(
  /**
   * @param {Object} props
   * @param {React.ReactNode} props.children
   * @param {string} [props.className]
   * @param {React.Ref<HTMLParagraphElement>} ref
   * @returns {JSX.Element}
   */
  ({ className, ...props }, ref) => (
    <DialogPrimitive.Description
      ref={ref}
      className={cn("text-base mt-2", "text-app-grey-1", className)}
      {...props}
    />
  )
);
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger };
